<template>
  <Select
      ref="select"
      show-search
      :placeholder="lang(placeholder)"
      :filterOption="props.remote ? false : filterOption"
      v-bind="$attrs"
      :label-in-value="props?.props?.valueObj === true"
      v-model:value="innerValue"
      @search="onSearchChange"
      :options="dic.value"
      @blur="selectBlur()"
  >
    <template v-if="fetching" #notFoundContent>
      <a-spin size="small"/>
    </template>
    <template #option="option">
      <LabelRender :option="option" :format="showLabel"/>
    </template>
  </Select>
</template>

<script lang="tsx" setup>
import {computed, inject, onMounted, type PropType, reactive, ref, watch} from 'vue'
import {Select} from 'ant-design-vue'
import LabelRender from './render'
const lang = inject('lang')

const emit = defineEmits(['update:value', 'change'])

const fetching = ref(false)
const searchValue = ref(undefined)

const showLabel = ({label} = {}) => label || ''

const props = defineProps({
  props: {
    type: Object as PropType<Record<any, any>>,
    default: () => ({
      label: 'label',
      value: 'value',
      valueObj: false
    })
  },
  options: {
    type: [Array as PropType<Record<any, any>[]>, Object],
    default: () => []
  },
  dicData: {
    type: [Array as PropType<Record<any, any>[]>, Object],
    default: () => []
  },
  dicUrl: {type: String, default: ''},
  dicMethod: {type: String, default: 'get'},
  dicQuery: Function as unknown as PropType<() => any>,
  dicQueryHeaders: Function as unknown as PropType<() => any>,
  dicFormat: Function as unknown as PropType<(list: any[]) => any[]>,
  placeholder: {type: String as PropType<string>, default: '请选择'},
  remote: {type: Boolean, default: false},
  change: Function as unknown as PropType<(v: any, i?: any) => void>,
  onQueryAsyncDict: Function as unknown as PropType<(q: string) => Promise<any[]>>,
  hasAll: {type: Boolean, default: false},     // 新增：是否插入“全部”
  allLabel: {type: String, default: '全部' },    // 新增：全部文本
  value: {type: null as any, default: undefined} // 接受 v-model:value
})

/** 保持你原来的 dic.value 访问方式（模板中用的是 dic.value） */
const dic = reactive({value: []})
const isInit = ref(false)
const defaultAllEmitted = ref(false) // 只在必要时发一次初始 null

// 把 dicData 格式化为 { label, value }，并在需要时在最前面插入 ALL
const initDict = () => {
  const raw = props.dicData && (props.dicData as any).value ? (props.dicData as any).value : props.dicData || []
  let list = (Array.isArray(raw) ? raw : []).map((i: any) => ({
    ...i,
    label: i[props.props.label],
    value: i[props.props.value]
  }))

  if (props.hasAll && list.length > 0) {
    list = [{label: lang(props.allLabel), value: '__ALL__'}, ...list]

    // 如果外层没有传 value（undefined），我们默认对外发送 null（表示全部）
    if ((props.value === undefined) && !defaultAllEmitted.value) {
      // 注意：我们把“全部”对外映射为 null（不会影响表格查询）
      emit('update:value', null)
      defaultAllEmitted.value = true
    }
  }

  dic.value = props.dicFormat ? props.dicFormat(list) : list
}

// 处理 remote 搜索返回时也插入 ALL
const onSearchChange = async (val: string) => {
  if (!props.onQueryAsyncDict) return
  fetching.value = true
  dic.value = []
  try {
    const rawList = await (props.onQueryAsyncDict as any)(val)
    let list = (Array.isArray(rawList) ? rawList : []).map((i: any) => ({
      ...i,
      label: i[props.props.label],
      value: i[props.props.value]
    }))

    if (props.hasAll && list.length > 0) {
      list = [{label: lang(props.allLabel), value: '__ALL__'}, ...list]
      if ((props.value === undefined) && !defaultAllEmitted.value) {
        emit('update:value', null)
        defaultAllEmitted.value = true
      }
    }

    dic.value = props.dicFormat ? props.dicFormat(list) : list
  } finally {
    fetching.value = false
  }
}

const selectBlur = () => {
  searchValue.value = undefined
}

// 关键：内部 v-model 的 computed，负责把 Select 的值（可能是 {label,value} 或 原始 value）
// 与外层 props.value（外部 v-model）映射起来；并在 set 时把 "__ALL__" 映射为 null 发出
const innerValue = computed({
  get() {
    const useLabelInValue = props?.props?.valueObj === true

    // 优先使用外层传入的 props.value（父组件控制）
    if (useLabelInValue) {
      // 外层没有传值 -> 默认显示“全部”（如果启用 hasAll）
      if (props.value === undefined) {
        return props.hasAll ? {value: '__ALL__', label: lang(props.allLabel)} : undefined
      }
      // 外层传 null -> 视作 ALL
      if (props.value === null) {
        return props.hasAll ? {value: '__ALL__', label: lang(props.allLabel)} : null
      }
      // 否则直接返回父级给的对象
      return props.value
    } else {
      if (props.value === undefined) {
        return props.hasAll ? '__ALL__' : undefined
      }
      if (props.value === null) {
        return props.hasAll ? '__ALL__' : null
      }
      return props.value
    }
  },
  set(v: any) {
    const useLabelInValue = props?.props?.valueObj === true

    // 选中 ALL 的内部表现为 '__ALL__' 或 {value:'__ALL__',label:'全部'}
    if (useLabelInValue) {
      if (v && v.value === '__ALL__') {
        emit('update:value', null)
        emit('change', null, null)
        if (props.change) props.change(null, null)
        return
      }
      // 非 ALL 的对象（完整的 label/value 对象）
      emit('update:value', v)
      // 找对应原始项（依据 props.props.value）
      const item = dic.value.find((i: any) => i[props.props.value] === (v && v.value))
      emit('change', v, item)
      if (props.change) props.change(v, item)
    } else {
      if (v === '__ALL__') {
        emit('update:value', null)
        emit('change', null, null)
        if (props.change) props.change(null, null)
        return
      }
      emit('update:value', v)
      const item = dic.value.find((i: any) => i[props.props.value] === v)
      emit('change', v, item)
      if (props.change) props.change(v, item)
    }
  }
})

const filterOption = (inputValue: string, option: Record<any, any>) => {
  searchValue.value = inputValue
  const val = String(inputValue || '').toLocaleUpperCase()
  const data = String(`${option?.label}${option?.value}`).toLocaleUpperCase()
  return data.indexOf(val) !== -1
}

watch(() => props.dicData, initDict, {immediate: true, deep: true})

onMounted(() => {
  if (props.dicUrl && !isInit.value && !props.remote) {
    isInit.value = true
    // 这里可以写异步请求逻辑并在拿到结果后调用 initDict 或直接赋值 dic.value
    dic.value = []
  } else {
    initDict()
  }
})
</script>
